import { setAuthorization } from "../shared/api.js";
import { config, devMode } from "./env.js";
import { forgetAll } from "../shared/keep.js";

/**
 * La connexion. En ligne : Firebase Auth (e-mail / mot de passe, Google si activé), le jeton
 * part en `Bearer`. En local sans configuration Firebase : une session « dev » (`Dev dev`),
 * gardée dans sessionStorage pour survivre au rechargement.
 * Le SDK Firebase n'est chargé que s'il sert (import dynamique) : l'app de dev reste légère.
 */

export type Session = { uid: string; email?: string | null; name?: string | null; dev: boolean };

type Listener = (s: Session | null) => void;
const listeners = new Set<Listener>();
let session: Session | null | undefined = undefined; // undefined = pas encore su
let signOutImpl: () => Promise<void> = async () => undefined;

function publish(s: Session | null) {
  session = s;
  listeners.forEach((l) => l(s));
}

export function onSession(l: Listener): () => void {
  listeners.add(l);
  if (session !== undefined) l(session);
  return () => void listeners.delete(l);
}

const DEV_KEY = "editit.dev-session";

async function firebaseAuth() {
  const [{ initializeApp, getApps }, auth] = await Promise.all([import("firebase/app"), import("firebase/auth")]);
  const app = getApps()[0] ?? initializeApp(config.firebase!);
  return { auth, instance: auth.getAuth(app) };
}

/** À appeler une fois au démarrage. */
export async function initAuth() {
  if (devMode) {
    let saved = false;
    try {
      saved = sessionStorage.getItem(DEV_KEY) === "1";
    } catch {
      /* rien */
    }
    setAuthorization(async () => (session ? "Dev dev" : null));
    signOutImpl = async () => {
      try {
        sessionStorage.removeItem(DEV_KEY);
      } catch {
        /* rien */
      }
      publish(null);
    };
    publish(saved ? { uid: "dev", dev: true, name: "Dev" } : null);
    return;
  }
  // La dernière session connue s'affiche tout de suite : Firebase relit le compte sur le réseau avant de
  // dire qui est connecté (350 ms à 3 s en ligne) et l'app attendait sur un écran vide. Les appels à
  // l'API, eux, attendent le vrai jeton ; si Firebase dit « personne », on revient à la connexion.
  const last = lastSession();
  if (last) publish(last);
  const ready = firebaseAuth();
  // Se déconnecter avant que Firebase soit chargé : on attend qu'il le soit (sinon le clic ne faisait rien).
  signOutImpl = async () => {
    const { auth, instance } = await ready;
    await auth.signOut(instance);
  };
  let settled = false;
  void ready
    .then(({ instance }) => instance.authStateReady())
    .then(
      () => (settled = true),
      () => (settled = true),
    );
  // Le jeton que Firebase a rangé sur l'appareil sert tant qu'il vaut encore (l'API le vérifie comme
  // un autre) : les premiers appels partent sans attendre que Firebase relise le compte en ligne.
  const early = last ? storedToken(last.uid) : Promise.resolve(null);
  setAuthorization(async () => {
    if (!settled) {
      const token = await early;
      if (token && !settled) return `Bearer ${token}`;
    }
    const { instance } = await ready;
    await instance.authStateReady();
    const u = instance.currentUser;
    return u ? `Bearer ${await u.getIdToken()}` : null;
  });
  const { auth, instance } = await ready;
  auth.onAuthStateChanged(instance, (u) => {
    const s = u ? { uid: u.uid, email: u.email, name: u.displayName, dev: false } : null;
    rememberSession(s);
    // Session finie autrement que par « Se déconnecter » (jeton révoqué, compte retiré) : rien ne reste non plus.
    if (!s) forgetAll();
    // Même compte que la session affichée : rien ne bouge (l'écran ne se remonte pas).
    if (s && session && session.uid === s.uid && session.email === s.email && session.name === s.name) return;
    publish(s);
  });
}

/**
 * Le jeton d'identité que Firebase garde dans IndexedDB (persistance par défaut du navigateur), s'il est
 * à ce compte et vaut encore au moins une minute. Un format inattendu ou un stockage absent : rien.
 */
function storedToken(uid: string): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const open = indexedDB.open("firebaseLocalStorageDb");
      open.onerror = () => resolve(null);
      open.onupgradeneeded = () => {
        open.transaction?.abort(); // la base n'existait pas : on ne la crée pas
        resolve(null);
      };
      open.onsuccess = () => {
        const db = open.result;
        try {
          const read = db
            .transaction("firebaseLocalStorage", "readonly")
            .objectStore("firebaseLocalStorage")
            .get(`firebase:authUser:${config.firebase!.apiKey}:[DEFAULT]`);
          read.onsuccess = () => {
            const user = (
              read.result as { value?: { uid?: string; stsTokenManager?: { accessToken?: string; expirationTime?: number } } } | undefined
            )?.value;
            const tm = user?.stsTokenManager;
            db.close();
            resolve(user?.uid === uid && tm?.accessToken && (tm.expirationTime ?? 0) - Date.now() > 60_000 ? tm.accessToken : null);
          };
          read.onerror = () => (db.close(), resolve(null));
        } catch {
          db.close();
          resolve(null);
        }
      };
    } catch {
      resolve(null);
    }
  });
}

const LAST_KEY = "editit.last-session";
function lastSession(): Session | null {
  try {
    const raw = localStorage.getItem(LAST_KEY);
    return raw ? ({ ...(JSON.parse(raw) as Session), dev: false } as Session) : null;
  } catch {
    return null;
  }
}
function rememberSession(s: Session | null) {
  try {
    if (s) localStorage.setItem(LAST_KEY, JSON.stringify({ uid: s.uid, email: s.email ?? null, name: s.name ?? null }));
    else localStorage.removeItem(LAST_KEY);
  } catch {
    /* rien */
  }
}

export async function signInDev() {
  try {
    sessionStorage.setItem(DEV_KEY, "1");
  } catch {
    /* rien */
  }
  publish({ uid: "dev", dev: true, name: "Dev" });
}

export async function signInEmail(email: string, password: string) {
  const { auth, instance } = await firebaseAuth();
  await auth.signInWithEmailAndPassword(instance, email, password);
}

export async function signInGoogle() {
  const { auth, instance } = await firebaseAuth();
  await auth.signInWithPopup(instance, new auth.GoogleAuthProvider());
}

export function signOut() {
  forgetAll();
  rememberSession(null);
  return signOutImpl();
}
